import type { Vault, IdentifiableVault, } from "@bracket/platform/types";



/**
 * Stores vault versions in localStorage for quick lookup
 * @param vaults - Array of vault objects containing address, symbol and version
 * @returns void
 * 
 * @remarks
 * Creates a lookup object mapping both vault addresses and symbols to their versions,
 * then stores this mapping in localStorage under the key "bracket_vaults"
 */
export const setVaultVersions = ( vaults: IdentifiableVault[] ): void => {
  const versions = vaults.reduce((acc, vault) => Object.assign(acc, { [vault.address]: vault.version, [vault.symbol as string]: vault.version }), {});
  globalThis.localStorage.setItem("bracket_vaults", JSON.stringify(versions));
}



/**
 * Retrieves the version of a vault from localStorage using either its address or symbol
 * @param identifier - The vault's address or symbol to look up
 * @returns The version number of the vault
 * @throws Error if the identifier is not found in localStorage
 * 
 * @remarks
 * This function:
 * 1. Checks if a valid identifier was provided
 * 2. Retrieves the versions mapping from localStorage
 * 3. Returns the version if found
 * 4. Throws an error if the identifier is not found
 */
export const getVaultVersion = ( identifier: Vault["address"] | Vault["symbol"] ) => {
  if(identifier) {
    const versions = JSON.parse(globalThis.localStorage.getItem("bracket_vaults") || "{}");
    if(versions && identifier in versions) {
      return versions[identifier];
    }  
  }
  throw new Error(`vault version not found for ${identifier}`);
}
